<div class="billing-page flex flex-col gap-6">
  <header>
    <h1 class="text-2xl font-semibold m-0">Billing</h1>
    <p class="text-sm text-surface-switch-600 mt-1 mb-0">
      Token usage and allocations for your organisation
    </p>
  </header>

  <p-card
    class="billing-section-card overflow-hidden border border-surface-switch-300"
  >
    <ng-template #header>
      <div
        class="flex justify-between items-center px-4 py-3 border-b border-surface-switch-300"
      >
        <div>
          <h6 class="m-0">Billing Period</h6>
          <p class="text-sm text-surface-switch-600 m-0 mt-1">
            Current usage cycle and reset schedule
          </p>
        </div>
      </div>
    </ng-template>

    <div class="flex flex-col gap-4 p-4">
      @if (nextResetDate(); as resetDate) {
        <div>
          @if (daysToReset() !== null) {
            Resets on {{ resetDate | date: "mediumDate" }}
            @if (daysToReset()! > 0) {
              (in {{ daysToReset() }}
              {{ daysToReset() === 1 ? "day" : "days" }})
            } @else if (daysToReset() === 0) {
              (today)
            } @else {
              ({{ -daysToReset()! }}
              {{ -daysToReset()! === 1 ? "day" : "days" }} ago)
            }
          } @else {
            Resets on {{ resetDate | date: "mediumDate" }}
          }
        </div>

        @if (periodStartDate(); as periodStart) {
          <p class="text-sm text-surface-switch-600 m-0">
            Period: {{ periodStart | date: "mediumDate" }} –
            {{ resetDate | date: "mediumDate" }}
          </p>
        }

        @if (billingCycleProgress() !== null) {
          <p-progress-bar
            [value]="billingCycleProgress()!"
            [showValue]="false"
          ></p-progress-bar>
        }
      } @else {
        <p class="text-surface-switch-600 m-0">
          Billing reset date is not configured.
        </p>
      }
    </div>
  </p-card>

  @if (tokenBillingEnabled()) {
    <p-card
      class="billing-section-card overflow-hidden border border-surface-switch-300"
    >
      <ng-template #header>
        <div
          class="flex justify-between items-center px-4 py-3 border-b border-surface-switch-300"
        >
          <div>
            <h6 class="m-0">On-Demand Usage</h6>
            <p class="text-sm text-surface-switch-600 m-0 mt-1">
              Pay-as-you-go when included tokens run out
            </p>
          </div>
          @if (
            onDemandEnabled() && onDemandPricingAvailable() && canEditOnDemand()
          ) {
            <button
              pButton
              iconOnly
              size="small"
              severity="secondary"
              aria-label="On-demand settings"
              (click)="openOnDemandSettings()"
            >
              <i class="p-button-icon pi pi-cog"></i>
            </button>
          }
        </div>
      </ng-template>

      @if (!onDemandPricingAvailable()) {
        <div class="flex flex-col items-center justify-center gap-3 p-4">
          <p class="m-0 text-sm text-surface-switch-600 text-center">
            On-demand usage is not available. Contact support if you need
            pay-as-you-go billing.
          </p>
        </div>
      } @else if (onDemandEnabled()) {
        <div class="flex flex-col gap-4 p-4">
          @if (onDemandBudgetAmount() > 0) {
            <div class="flex flex-col gap-1">
              <span class="text-sm text-surface-switch-600">
                Spent this period: {{ onDemandSpend() | number: "1.2-4" }} /
                {{ onDemandBudgetAmount() | number: "1.2-4" }}
                {{ currencyCode() }}
              </span>
              <p-progress-bar
                [value]="onDemandSpendProgress()"
                [showValue]="false"
              ></p-progress-bar>
            </div>
          }

          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium">Per-token prices</span>
            @for (price of tokenOnDemandPrices(); track price.tokenType) {
              <span class="text-sm text-surface-switch-600">
                {{ tokenTypeLabel(price.tokenType) }}:
                {{ price.price | number: "1.2-4" }} {{ currencyCode() }}
              </span>
            }
          </div>
        </div>
      } @else {
        <div class="flex flex-col items-center justify-center gap-3 p-4">
          <p class="m-0 text-sm text-surface-switch-600 text-center">
            On-demand usage is disabled.
          </p>
          @if (canEditOnDemand()) {
            <button pButton type="button" (click)="openEnableOnDemand()">
              Enable
            </button>
          }
        </div>
      }
    </p-card>
  }

  <p-card
    class="billing-section-card overflow-hidden border border-surface-switch-300"
  >
    <ng-template #header>
      <div
        class="flex justify-between items-center px-4 py-3 border-b border-surface-switch-300"
      >
        <div>
          <h6 class="m-0">Available Tokens</h6>
          <p class="text-sm text-surface-switch-600 m-0 mt-1">
            Active grants for this billing period
          </p>
        </div>
        @if (userService.user()?.role === "superuser") {
          <div>
            <button
              pButton
              size="small"
              (click)="addTokensDialogVisible = true"
            >
              <i class="pi pi-plus"></i>
              Add Tokens
            </button>
          </div>
        }
      </div>
    </ng-template>

    <p-table
      [value]="billingService.tokenAllocations.value() ?? []"
      [tableStyle]="{ 'min-width': '50rem' }"
    >
      <ng-template #header>
        <tr>
          <th>Token Type</th>
          <th>Usage</th>
          <th>Remaining</th>
          <th>Valid until</th>
          <th>Allocation</th>
        </tr>
      </ng-template>
      <ng-template #body let-allocation>
        <tr>
          <td>{{ tokenTypeLabel(allocation.token_type) }}</td>
          <td>
            <div class="flex flex-col gap-1 min-w-40">
              <span class="text-sm text-surface-switch-600">
                {{ allocation.allocation_remaining }} /
                {{ allocation.allocated_amount }} remaining
              </span>
              <p-progress-bar
                [value]="allocationUsagePercent(allocation)"
                [showValue]="false"
                styleClass="h-2"
              ></p-progress-bar>
            </div>
          </td>
          <td>{{ allocation.allocation_remaining }}</td>
          <td>{{ allocation.valid_to | date: "mediumDate" }}</td>
          <td>{{ allocationTypeLabel(allocation.allocation_type) }}</td>
        </tr>
      </ng-template>
      <ng-template #emptymessage>
        <tr>
          <td colspan="5" class="text-center text-surface-switch-600">
            No active token allocations.
          </td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>

  <p-card
    class="billing-section-card overflow-hidden border border-surface-switch-300"
  >
    <ng-template #header>
      <div
        class="flex justify-between items-center px-4 py-3 border-b border-surface-switch-300"
      >
        <div>
          <h6 class="m-0">Token Usage</h6>
          <p class="text-sm text-surface-switch-600 m-0 mt-1">
            Recent token activity
          </p>
        </div>
        <button
          pButton
          iconOnly
          size="small"
          severity="secondary"
          [disabled]="usageLoading()"
          (click)="refreshUsage()"
          aria-label="Refresh token usage"
        >
          @if (usageLoading()) {
            <i class="p-button-icon pi pi-spin pi-spinner"></i>
          } @else {
            <i class="p-button-icon pi pi-refresh"></i>
          }
        </button>
      </div>
    </ng-template>

    <div class="px-4 pb-4">
      <div
        class="flex flex-wrap gap-4 text-sm rounded-lg border border-surface-switch-300 bg-surface-switch-50 px-4 py-3"
      >
        <span>
          <span class="text-surface-switch-600">This period:&nbsp;</span>
          <span class="font-medium">{{ usageSummary().consumed }}</span>
          consumed
        </span>
        <span>
          <span class="text-green-600 font-medium">{{
            usageSummary().refunded
          }}</span>
          refunded
        </span>
        <span>
          <span class="text-red-600 font-medium">{{
            usageSummary().failed
          }}</span>
          failed
        </span>
      </div>
    </div>

    <p-table
      [value]="tokenUseEvents()"
      [loading]="usageLoading()"
      [tableStyle]="{ 'min-width': '50rem' }"
    >
      <ng-template #header>
        <tr>
          <th>Date</th>
          <th>Token type</th>
          <th>Event</th>
          <th>Quantity</th>
          <th>User</th>
          <th>Source</th>
          @if (isSuperuser()) {
            <th>Actions</th>
          }
        </tr>
      </ng-template>
      <ng-template #body let-event>
        <tr>
          <td>
            <span
              [pTooltip]="(event.timestamp | date: 'medium') ?? ''"
              tooltipPosition="top"
            >
              {{ relativeTimestamp(event.timestamp) }}
            </span>
          </td>
          <td>{{ tokenTypeLabel(event.token_type) }}</td>
          <td>
            <p-tag
              [value]="useTypeLabel(event.use_type)"
              [severity]="useTypeSeverity(event.use_type)"
            />
          </td>
          <td [class]="quantityDisplay(event).className">
            {{ quantityDisplay(event).text }}
          </td>
          <td>{{ event.user_email }}</td>
          <td>
            @if (event.source_path) {
              <a
                [routerLink]="event.source_path"
                class="text-primary hover:underline"
              >
                {{ event.source_label }}
              </a>
            } @else {
              {{ event.source_label }}
            }
          </td>
          @if (isSuperuser()) {
            <td>
              @if (canRefundEvent(event)) {
                <button
                  pButton
                  type="button"
                  size="small"
                  severity="secondary"
                  label="Refund"
                  (click)="confirmRefund(event)"
                ></button>
              }
            </td>
          }
        </tr>
      </ng-template>
      <ng-template #emptymessage>
        <tr>
          <td
            [attr.colspan]="isSuperuser() ? 7 : 6"
            class="text-center text-surface-switch-600"
          >
            No token usage events yet.
          </td>
        </tr>
      </ng-template>
    </p-table>

    <p-paginator
      [first]="usageFirst"
      [rows]="usageRows"
      [totalRecords]="tokenUseEventsTotal()"
      [rowsPerPageOptions]="[10, 20, 50]"
      (onPageChange)="onUsagePageChange($event)"
    />
  </p-card>

  <shared-confirm-dialog />

  <app-add-tokens-dialog
    [(visible)]="addTokensDialogVisible"
    (saved)="onTokensAdded()"
  />

  <app-on-demand-settings-dialog
    [(visible)]="onDemandSettingsDialogVisible"
    [currencyCode]="currencyCode()"
    [currentlyEnabled]="onDemandEnabled()"
    [initialBudget]="onDemandBudget()"
    [enableOnSave]="onDemandDialogEnableOnSave"
  />
</div>
